import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import QRCode from 'qrcode';

/**
 * Generates PDF with location codes formatted for Avery 22838 label sheets
 * Template: Avery 22838 (5 labels per page, 7.85" x 1.75" each)
 * Page size: Letter (8.5" x 11")
 */
export const generateLocationCodesPDF = async (locations, boxId) => {
  const pdfDoc = await PDFDocument.create();
  const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const helveticaBoldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  // Avery 22838 template specifications
  const itemsPerPage = 5; // 5 labels per page
  const pageWidth = 612; // Letter width: 8.5" (612 points)
  const pageHeight = 792; // Letter height: 11" (792 points)
  const labelWidth = 565.2; // Label width: 7.85" (565.2 points)
  const labelHeight = 126; // Label height: 1.75" (126 points)
  const leftMargin = 22.5; // Left margin: 0.3125" (22.5 points)
  const labelPositions = [36, 180, 324, 468, 612]; // Label Y positions from top: 0.5", 2.5", 4.5", 6.5", 8.5"
  const qrSize = 111.6;
  const labelPaddingTop = 7.2;
  const labelPaddingLeft = 10.8;
  const qrTextGap = 18;
  const baseFontSize = 110;
  const locationCodeFontRatio = 0.4;
  const textSpacing = 8;
  const minFontSize = 50;

  const leftQrX = leftMargin + labelPaddingLeft;
  const rightQrX = leftMargin + labelWidth - qrSize - labelPaddingLeft;
  const leftTextX = leftQrX + qrSize + qrTextGap;
  const rightTextX = rightQrX - qrTextGap;
  const availableTextWidth = (rightTextX - leftTextX) / 2;
  const availableTextHeight = labelHeight - (labelPaddingTop * 2);

  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (result) {
      return {
        r: parseInt(result[1], 16) / 255,
        g: parseInt(result[2], 16) / 255,
        b: parseInt(result[3], 16) / 255
      };
    }
    return null;
  };

  const expandHex = (hex) => {
    if (hex.length === 4 && hex[0] === '#') {
      return `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
    }
    return hex;
  };

  const measureTextWidth = (text, fontSize, font) => {
    return font.widthOfTextAtSize(text, fontSize);
  };

  const maxLabel = 'A1000';
  const locationTextWidth = measureTextWidth(maxLabel, baseFontSize, helveticaBoldFont);
  const locationTextHeight = baseFontSize * 1.2;
  const locationCodeBaseSize = baseFontSize * locationCodeFontRatio;
  const locationCodeTextWidth = measureTextWidth(maxLabel, locationCodeBaseSize, helveticaFont);
  const locationCodeTextHeight = locationCodeBaseSize * 1.2;
  const normalizedLocationCodeWidth = locationCodeTextWidth * (baseFontSize / locationCodeBaseSize);
  const longestTextWidth = Math.max(locationTextWidth, normalizedLocationCodeWidth);
  const totalTextHeight = locationTextHeight + textSpacing + locationCodeTextHeight;
  
  const horizontalScale = longestTextWidth > availableTextWidth 
    ? availableTextWidth / longestTextWidth 
    : 1;
  const verticalScale = totalTextHeight > availableTextHeight 
    ? availableTextHeight / totalTextHeight 
    : 1;
  const scaleFactor = Math.min(horizontalScale, verticalScale);
  const consistentFontSize = Math.max(minFontSize, baseFontSize * scaleFactor);
  const consistentLocationCodeSize = consistentFontSize * locationCodeFontRatio;

  const qrImages = await Promise.all(
    locations.map(async (location) => {
      const qrDataUrl = await QRCode.toDataURL(location.location_code, {
        width: qrSize,
        margin: 2,
        errorCorrectionLevel: 'M',
        color: { dark: '#000000', light: '#FFFFFF' }
      });
      const qrResponse = await fetch(qrDataUrl);
      const qrUint8Array = new Uint8Array(await qrResponse.arrayBuffer());
      return await pdfDoc.embedPng(qrUint8Array);
    })
  );

  let itemCount = 0;
  let currentPage = null;

  for (let i = 0; i < locations.length; i++) {
    const location = locations[i];
    const qrImage = qrImages[i];
    
    if (itemCount % itemsPerPage === 0) {
      currentPage = pdfDoc.addPage([pageWidth, pageHeight]);
    }

    const labelIndex = itemCount % itemsPerPage;
    const labelYFromBottom = pageHeight - labelPositions[labelIndex] - labelHeight;
    const labelX = leftMargin;

    let colorCode = location.color_code || '#000000';
    colorCode = expandHex(colorCode.trim());
    const colorRgb = hexToRgb(colorCode);
    const bgColor = colorRgb && !isNaN(colorRgb.r) && !isNaN(colorRgb.g) && !isNaN(colorRgb.b)
      ? rgb(colorRgb.r, colorRgb.g, colorRgb.b)
      : rgb(0, 0, 0);
    const textColor = rgb(1, 1, 1);

    currentPage.drawRectangle({
      x: labelX,
      y: labelYFromBottom,
      width: labelWidth,
      height: labelHeight,
      color: bgColor,
    });

    const leftQrYFromBottom = labelYFromBottom + labelPaddingTop + (labelHeight - labelPaddingTop * 2 - qrSize) / 2;
    const textBlockCenterYFromBottom = labelYFromBottom + labelHeight / 2;
    const locationTextYFromBottom = textBlockCenterYFromBottom + (consistentLocationCodeSize + textSpacing) / 2;
    const locationCodeYFromBottom = textBlockCenterYFromBottom - (consistentFontSize + textSpacing) / 2;

    currentPage.drawImage(qrImage, {
      x: leftQrX,
      y: leftQrYFromBottom,
      width: qrSize,
      height: qrSize,
    });

    currentPage.drawImage(qrImage, {
      x: rightQrX,
      y: leftQrYFromBottom,
      width: qrSize,
      height: qrSize,
    });

    currentPage.drawText(location.location, {
      x: leftTextX,
      y: locationTextYFromBottom,
      size: consistentFontSize,
      font: helveticaBoldFont,
      color: textColor,
      maxWidth: availableTextWidth,
    });

    currentPage.drawText(location.location_code || '', {
      x: leftTextX,
      y: locationCodeYFromBottom,
      size: consistentLocationCodeSize,
      font: helveticaFont,
      color: textColor,
      maxWidth: availableTextWidth,
    });

    const rightTextAreaStartX = leftTextX + availableTextWidth;
    const rightTextAreaEndX = rightTextAreaStartX + availableTextWidth;
    const rightLocationTextWidth = measureTextWidth(location.location, consistentFontSize, helveticaBoldFont);
    const rightLocationTextX = Math.max(rightTextAreaStartX, rightTextAreaEndX - rightLocationTextWidth);
    
    currentPage.drawText(location.location, {
      x: rightLocationTextX,
      y: locationTextYFromBottom,
      size: consistentFontSize,
      font: helveticaBoldFont,
      color: textColor,
      maxWidth: availableTextWidth,
    });

    const rightLocationCodeWidth = measureTextWidth(location.location_code || '', consistentLocationCodeSize, helveticaFont);
    const rightLocationCodeX = Math.max(rightTextAreaStartX, rightTextAreaEndX - rightLocationCodeWidth);
    
    currentPage.drawText(location.location_code || '', {
      x: rightLocationCodeX,
      y: locationCodeYFromBottom,
      size: consistentLocationCodeSize,
      font: helveticaFont,
      color: textColor,
      maxWidth: availableTextWidth,
    });

    if ((itemCount + 1) % itemsPerPage !== 0 && i < locations.length - 1) {
      currentPage.drawLine({
        start: { x: labelX, y: labelYFromBottom },
        end: { x: labelX + labelWidth, y: labelYFromBottom },
        thickness: 0.5,
        color: rgb(0.8, 0.8, 0.8),
      });
    }

    itemCount++;
  }

  const pdfBytes = await pdfDoc.save();
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `location-codes-${boxId}-${new Date().getTime()}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};



// WEBPACK FOOTER //
// ./src/components/bin-mapping/pdfGenerator.js